import { useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";

interface SEOHeadProps {
  slug: string;
}

const BASE_URL = "https://ptahub.co.uk";

export function SEOHead({ slug }: SEOHeadProps) {
  const { seoData } = useSEO(slug);

  useEffect(() => {
    // Update document title
    document.title = seoData.title;

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement | null;
      if (element) {
        element.setAttribute(attribute === "content" ? "content" : attribute, value);
      } else {
        element = document.createElement("meta");
        if (selector.includes("property=")) {
          const propMatch = selector.match(/property="([^"]+)"/);
          if (propMatch) element.setAttribute("property", propMatch[1]);
        } else if (selector.includes("name=")) {
          const nameMatch = selector.match(/name="([^"]+)"/);
          if (nameMatch) element.setAttribute("name", nameMatch[1]);
        }
        element.setAttribute("content", value);
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (element) {
        element.href = href;
      } else {
        element = document.createElement("link");
        element.rel = rel;
        element.href = href;
        document.head.appendChild(element);
      }
    };

    // Update meta description
    updateMetaTag('meta[name="description"]', "content", seoData.description);

    // Update meta keywords
    updateMetaTag('meta[name="keywords"]', "content", seoData.keywords);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', "content", seoData.og_title);
    updateMetaTag('meta[property="og:description"]', "content", seoData.og_description);
    updateMetaTag('meta[property="og:image"]', "content", seoData.og_image);
    updateMetaTag('meta[property="og:url"]', "content", `${BASE_URL}${slug === "landing" ? "" : `/${slug}`}`);

    // Update Twitter tags
    updateMetaTag('meta[name="twitter:title"]', "content", seoData.og_title);
    updateMetaTag('meta[name="twitter:description"]', "content", seoData.og_description);
    updateMetaTag('meta[name="twitter:image"]', "content", seoData.og_image);

    // Update canonical URL
    updateLinkTag("canonical", `${BASE_URL}${slug === "landing" ? "" : `/${slug}`}`);
  }, [seoData, slug]);

  return null;
}
